var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { EntityRemindersQuery } from 'graphql/EntityReminder/queries/EntityRemindersQuery';
import { CreateEntityReminderMutation } from 'graphql/EntityReminder/mutations/CreateEntityReminderMutation';
import { formatDueDate, formatFullDateTime, reminderBucket } from 'helpers/entityReminder';
import { classNames, React, useEffect, useMutation, useQuery, useState } from 'libs/react';
import { ActivityComposer } from 'components/activity/ActivityComposer';
import { ActivityEmpty } from 'components/activity/ActivityEmpty';
import { ActivityMenu } from 'components/activity/ActivityMenu';
import { ActivityText } from 'components/activity/ActivityText';
import { useReminderActions } from 'components/activity/useReminderActions';
import { Icon } from 'components/Icon';
import { useAlert } from 'components/alert/AlertStack';
import { UserImage } from 'components/user/UserImage';
import './EntityReminders.scss';
/**
 * @const SECTIONS
 * @since 1.0.0
 */
const SECTIONS = [
    { bucket: 'overdue', label: () => t('Overdue') },
    { bucket: 'today', label: () => t('Today') },
    { bucket: 'upcoming', label: () => t('Upcoming') }
];
/**
 * @component EntityReminders
 * @since 1.0.0
 */
export function EntityReminders(props) {
    var _a;
    const { entity, entityId, onPendingCountChange } = props;
    const alert = useAlert();
    const [editingId, setEditingId] = useState(null);
    const [showCompleted, setShowCompleted] = useState(false);
    const [data, ready, , , refetch] = useQuery(EntityRemindersQuery, {
        variables: {
            input: {
                entity,
                entityId
            }
        },
        fetchPolicy: 'network-only',
        skip: !entityId
    });
    const [createReminder] = useMutation(CreateEntityReminderMutation);
    const actions = useReminderActions(() => refetch());
    const reminders = (_a = data === null || data === void 0 ? void 0 : data.entityReminders) !== null && _a !== void 0 ? _a : [];
    const buckets = {
        overdue: [],
        today: [],
        upcoming: [],
        completed: []
    };
    for (const reminder of reminders) {
        buckets[reminderBucket(reminder)].push(reminder);
    }
    const pendingCount = buckets.overdue.length + buckets.today.length + buckets.upcoming.length;
    useEffect(() => {
        onPendingCountChange === null || onPendingCountChange === void 0 ? void 0 : onPendingCountChange(pendingCount, buckets.overdue.length);
    }, [pendingCount, buckets.overdue.length, onPendingCountChange]);
    /**
     * @function onCreate
     * @since 1.0.0
     */
    function onCreate(content, dueAt) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!entityId || !dueAt)
                return;
            try {
                yield createReminder({
                    variables: {
                        input: {
                            entity,
                            entityId,
                            content,
                            dueAt
                        }
                    }
                });
                refetch();
            }
            catch (error) {
                alert.present({ error });
                throw error;
            }
        });
    }
    /**
     * @function onEdit
     * @since 1.0.0
     */
    function onEdit(reminder, content, dueAt) {
        return __awaiter(this, void 0, void 0, function* () {
            const dueChanged = !!dueAt &&
                new Date(dueAt).getTime() !== new Date(reminder.dueAt).getTime();
            try {
                // An unchanged due date is left out so the reminder does not fire twice
                yield actions.update(reminder.id, {
                    content,
                    dueAt: dueChanged ? dueAt : undefined
                });
                setEditingId(null);
            }
            catch (error) {
                alert.present({ error });
                throw error;
            }
        });
    }
    /**
     * @function onMenuSelect
     * @since 1.0.0
     */
    function onMenuSelect(reminder, value) {
        if (value === 'edit') {
            setEditingId(reminder.id);
        }
        else {
            actions.select(reminder, value);
        }
    }
    /**
     * @function renderReminder
     * @since 1.0.0
     */
    function renderReminder(reminder) {
        const bucket = reminderBucket(reminder);
        const completed = bucket === 'completed';
        const busy = actions.busyId === reminder.id;
        return (React.createElement("li", { key: reminder.id, className: classNames('entity-reminders-item', 'entity-reminders-item--' + bucket) },
            React.createElement("button", { type: "button", className: "entity-reminders-check", "aria-label": completed ? t('Mark as pending') : t('Mark as done'), "aria-pressed": completed, disabled: busy, onClick: () => actions.toggle(reminder) }, busy ? (React.createElement(Icon, { className: "entity-reminders-check-spin", value: "fal spinner-third" })) : (React.createElement(Icon, { value: completed ? 'fas circle-check' : 'fal circle' }))),
            React.createElement("div", { className: "entity-reminders-item-main" }, editingId === reminder.id ? (React.createElement(ActivityComposer, { placeholder: t('Add a reminder…'), submitLabel: t('Save'), withDueDate: true, defaultContent: reminder.content, defaultDueAt: reminder.dueAt, onSubmit: (content, dueAt) => onEdit(reminder, content, dueAt), onCancel: () => setEditingId(null) })) : (React.createElement(React.Fragment, null,
                React.createElement(ActivityText, { value: reminder.content }),
                React.createElement("div", { className: "entity-reminders-item-meta" },
                    React.createElement("span", { className: "entity-reminders-item-date", title: formatFullDateTime(reminder.dueAt) },
                        React.createElement(Icon, { value: "fal bell" }),
                        formatDueDate(reminder.dueAt)),
                    reminder.author && (React.createElement("span", { className: "entity-reminders-item-author", title: reminder.author.fullname },
                        React.createElement(UserImage, { user: reminder.author }))))))),
            editingId !== reminder.id && (React.createElement(ActivityMenu, { className: "entity-reminders-item-menu", items: actions.menuItems(reminder, { edit: true }), onSelect: (value) => onMenuSelect(reminder, value) }))));
    }
    return (React.createElement("div", { className: "entity-reminders" },
        React.createElement(ActivityComposer, { className: "entity-reminders-composer", placeholder: t('Add a reminder…'), submitLabel: t('Add reminder'), withDueDate: true, onSubmit: onCreate }),
        React.createElement("div", { className: "entity-reminders-body" },
            pendingCount === 0 && ready && (React.createElement(ActivityEmpty, { picto: "fal bell", title: t('Nothing to follow up'), description: t('Set a reminder and you will be notified when it is due.') })),
            SECTIONS.map(({ bucket, label }) => buckets[bucket].length > 0 ? (React.createElement("section", { key: bucket, className: classNames('entity-reminders-group', 'entity-reminders-group--' + bucket) },
                React.createElement("h4", { className: "entity-reminders-group-label" },
                    label(),
                    React.createElement("span", { className: "entity-reminders-group-count" }, buckets[bucket].length)),
                React.createElement("ul", { className: "entity-reminders-list" }, buckets[bucket].map(renderReminder)))) : null),
            buckets.completed.length > 0 && (React.createElement("section", { className: "entity-reminders-group entity-reminders-group--completed" },
                React.createElement("button", { type: "button", className: "entity-reminders-group-label entity-reminders-group-toggle", "aria-expanded": showCompleted, onClick: () => setShowCompleted(!showCompleted) },
                    React.createElement(Icon, { value: showCompleted ? 'fal chevron-down' : 'fal chevron-right' }),
                    t('Completed'),
                    React.createElement("span", { className: "entity-reminders-group-count" }, buckets.completed.length)),
                showCompleted && (React.createElement("ul", { className: "entity-reminders-list" }, buckets.completed.map(renderReminder))))))));
}
